import React, { useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import type { Language, Translations } from './types';
import {
  LanguageContext,
  LanguageContextType,
  EMERGENCY_FALLBACK_TRANSLATIONS,
  loadTranslations,
} from './LanguageContext';
import { logger } from '@/utils/debugLogger';

interface LanguageProviderProps {
  children: ReactNode;
  /** A fixed starting language for isolated specimens whose copy is in one language. */
  initialLanguage?: Language;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children, initialLanguage }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    if (initialLanguage) return initialLanguage;
    // Get saved language from localStorage or default to Finnish
    const saved = localStorage.getItem('forestModeler_language');
    return (saved as Language) || 'fi';
  });

  const [translations, setTranslations] = useState<Translations | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load translations when language changes
  useEffect(() => {
    const loadLanguageTranslations = async () => {
      setIsLoading(true);
      try {
        const newTranslations = await loadTranslations(language);
        setTranslations(newTranslations);
      } catch (error) {
        logger.error(
          { component: 'LanguageProvider', action: 'loadLanguageTranslations' },
          'Failed to load translations',
          error,
        );
        // Try to load English as fallback
        try {
          logger.info(
            { component: 'LanguageProvider', action: 'loadLanguageTranslations' },
            'Loading English as fallback...',
          );
          const fallbackTranslations = await loadTranslations('en');
          setTranslations(fallbackTranslations);
        } catch (fallbackError) {
          logger.error(
            { component: 'LanguageProvider', action: 'loadLanguageTranslations' },
            'Failed to load fallback translations',
            fallbackError,
          );
          // Provide minimal working translations to prevent crashes
          setTranslations(EMERGENCY_FALLBACK_TRANSLATIONS);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadLanguageTranslations();
  }, [language]);

  // Update document.documentElement.lang when active language changes
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('forestModeler_language', lang);
  }, []);

  // Memoize the context value so i18n consumers only re-render when language,
  // translations, or isLoading actually change — NOT on every provider render.
  // A switch flips isLoading true→false around the async load; without this memo
  // every useLanguage consumer (incl. the Leaflet base-layer control) re-rendered
  // on each of those transitions, churning react-leaflet's stateful LayersControl
  // and feeding a "Maximum update depth" burst.
  const value = useMemo<LanguageContextType>(
    () => ({
      language,
      setLanguage,
      t: translations || EMERGENCY_FALLBACK_TRANSLATIONS,
      isLoading,
    }),
    [language, setLanguage, translations, isLoading],
  );

  // Only block rendering on the very first load (no translations yet).
  // During language switches translations is non-null (previous language),
  // so children stay mounted and the UI updates once new translations arrive.
  const showBlocker = isLoading && translations === null;

  return (
    <LanguageContext.Provider value={value}>
      {showBlocker ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: 'var(--fm-space-4)'
        }}>
          <div>Loading translations...</div>
        </div>
      ) : (
        children
      )}
    </LanguageContext.Provider>
  );
};
